import React, { useState } from 'react';
import { FiSearch, FiPhone, FiUser, FiMapPin, FiMail, FiLoader, FiAlertCircle, FiCheckCircle, FiCreditCard, FiUsers, FiHome } from 'react-icons/fi';
import Calling from '../../api/Calling';

const MobileFetch = () => {
    const [mobile, setMobile] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const handleSearch = async (e) => {
        e.preventDefault();

        // Validation
        const cleanedMobile = mobile.trim();
        if (!cleanedMobile) {
            setError("Please enter a mobile number");
            return;
        }

        if (!/^\d{10}$/.test(cleanedMobile)) {
            setError("Please enter a valid 10-digit mobile number");
            return;
        }

        setLoading(true);
        setError("");
        setResult(null);

        try {
            const resp = await Calling.mobileFetch(cleanedMobile);

            if (resp.status) {
                setResult(resp.data);
            } else {
                setError(resp.message || "Mobile number not found");
            }
        } catch (err) {
            setError("Something went wrong. Please try again.");
        }

        setLoading(false);
    };

    const handleClear = () => {
        setMobile("");
        setResult(null);
        setError("");
    };

    const InfoRow = ({ icon: Icon, label, value }) => {
        if (!value) return null;
        return (
            <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
                <div className="w-8 h-8 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="text-main-blue text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-sm font-medium text-main-font mt-0.5 break-words">{value}</p>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full text-main-font mt-20 px-3 md:ml-[4.4rem]">
            <div className="bg-main-white shadow-md rounded-xl p-6 md:p-8 w-full border border-main-border">

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-main-blue/10 rounded-xl flex items-center justify-center">
                        <FiSearch className="text-main-blue text-xl" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-main-font">Search Mobile Number</h2>
                        <p className="text-sm text-gray-500">Find details by mobile number</p>
                    </div>
                </div>

                {/* Search Form */}
                <form onSubmit={handleSearch}>
                    <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                            <FiPhone className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={mobile}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setMobile(val);
                                setError("");
                            }}
                            placeholder="Enter 10-digit mobile number"
                            className="w-full pl-11 pr-4 py-3.5 border border-main-border rounded-xl
                                       focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:border-main-blue
                                       placeholder-gray-400 text-base font-mono"
                        />
                        {mobile && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Character Count */}
                    <div className="flex justify-end mt-2">
                        <span className={`text-xs ${mobile.length === 10 ? 'text-green-500' : 'text-gray-400'}`}>
                            {mobile.length}/10 digits
                        </span>
                    </div>

                    {/* Search Button */}
                    <button
                        type="submit"
                        disabled={loading || mobile.length !== 10}
                        className={`w-full mt-4 py-3.5 rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center gap-2
                            ${loading || mobile.length !== 10
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-main-blue hover:bg-main-blue/90 hover:shadow-lg hover:shadow-main-blue/25'
                            }`}
                    >
                        {loading ? (
                            <>
                                <FiLoader className="animate-spin" />
                                Searching...
                            </>
                        ) : (
                            <>
                                <FiSearch />
                                Search
                            </>
                        )}
                    </button>
                </form>

                {/* Error Message */}
                {error && (
                    <div className="flex items-center gap-3 p-4 mt-6 rounded-xl bg-red-50 border border-red-200">
                        <FiAlertCircle className="text-red-500 text-xl flex-shrink-0" />
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                )}

                {/* Result Display */}
                {result && (
                    <div className="mt-6 animate-fadeIn">
                        {/* Success Header */}
                        <div className="flex items-center gap-2 mb-4">
                            <FiCheckCircle className="text-green-500" />
                            <span className="text-sm font-medium text-green-600">Record Found</span>
                        </div>

                        {/* Main Info Card */}
                        <div className="bg-gradient-to-br from-main-blue to-blue-600 rounded-xl p-5 text-white mb-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                    <FiUser className="text-2xl" />
                                </div>
                                <div>
                                    <p className="font-semibold text-lg">{result.m_name || 'N/A'}</p>
                                    <p className="text-white/80 text-sm font-mono">{result.m_number}</p>
                                </div>
                            </div>
                            {result.m_circle && (
                                <div className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1.5 rounded-full text-sm">
                                    <FiPhone className="text-xs" />
                                    {result.m_circle}
                                </div>
                            )}
                        </div>

                        {/* Details Card */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-main-border">
                            <h4 className="text-sm font-semibold text-main-font mb-3">Details</h4>

                            <InfoRow
                                icon={FiPhone}
                                label="Alternate Number"
                                value={result.m_alt_number}
                            />

                            <InfoRow
                                icon={FiUsers}
                                label="Father's Name"
                                value={result.m_fname}
                            />

                            <InfoRow
                                icon={FiCreditCard}
                                label="UID (Aadhaar)"
                                value={result.m_uid}
                            />

                            <InfoRow
                                icon={FiMail}
                                label="Email"
                                value={result.m_email}
                            />

                            <InfoRow
                                icon={FiHome}
                                label="Address"
                                value={result.m_address}
                            />

                            {result.m_sub_name && result.m_sub_name.length > 0 && (
                                <div className="py-3 border-b border-gray-100 last:border-0">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiUser className="text-main-blue text-sm" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-gray-500">Subscriber Names</p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {result.m_sub_name.map((name, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="bg-white px-3 py-1 rounded-lg text-xs font-medium border border-gray-200"
                                                    >
                                                        {name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Timestamps */}
                        <div className="flex items-center justify-between mt-4 text-xs text-gray-400">
                            <span>Added: {new Date(result.createdAt).toLocaleDateString()}</span>
                            <span>Updated: {new Date(result.updatedAt).toLocaleDateString()}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Custom Animation Styles */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default MobileFetch;